import { compareAsc, compareDesc } from "date-fns";
import { useMemo } from "react";

import { Button, Select } from "@smart/itops-sb-design-system-dom";

import { calendarMonthsShort } from "../../../../types";
import { convertToZonedTime } from "../../../../utils";

type MonthPickerProps = {
  year: number;
  month: number;
  yearRange?: number;
  disabled?: boolean;
  onChange: (value: { year: number; month: number }) => void;
  viewingTimezone?: string | null;
};

export const MonthPicker = ({
  year,
  month,
  onChange,
  yearRange = 2,
  disabled,
  viewingTimezone,
}: MonthPickerProps) => {
  const today = convertToZonedTime(new Date(), viewingTimezone);
  const currentYear = today.getFullYear();

  const yearOptions = useMemo(() => {
    const options = [];
    for (let count = 0; count < yearRange; count += 1) {
      options.push(currentYear + count);
    }

    return options;
  }, [yearRange, currentYear]);
  const earliestDate = new Date(yearOptions[0], 0, 1);
  const latestDate = new Date(yearOptions[yearRange - 1] + 1, 0, 1);

  const isOutOfRange = (date: Date) =>
    compareDesc(date, latestDate) !== 1 ||
    compareAsc(date, earliestDate) === -1;

  const changeMonth = ({
    toMonth,
    toYear,
    byDistance = 0,
  }: {
    toMonth?: number;
    toYear?: number;
    byDistance?: number;
  }) => {
    const yearToUse = toYear || year;
    const monthToUse = toMonth === undefined ? month : toMonth;
    const date = new Date(yearToUse, monthToUse + byDistance, 1);

    if (isOutOfRange(date)) return;

    onChange({
      year: date.getFullYear(),
      month: date.getMonth(),
    });
  };

  return (
    <div className="flex w-full items-center justify-between">
      <Button
        leftIcon="regularChevronLeft"
        variant="secondarySubtle"
        onClick={() => !disabled && changeMonth({ byDistance: -1 })}
        disabled={disabled}
        aria-label="Previous month"
      />
      <div className="flex gap-small">
        <Select
          id="month-selection"
          title="Month"
          options={calendarMonthsShort.map((m) => ({ value: m, label: m }))}
          value={calendarMonthsShort[month]}
          onChange={async (v) => {
            const toMonth = calendarMonthsShort.findIndex((m) => m === v);
            changeMonth({ toMonth });
          }}
          disabled={disabled}
          className="max-w-[8rem] text-ui font-semibold"
        />
        <Select
          id="year-selection"
          title="Year"
          options={yearOptions.map((y) => ({ value: y, label: y.toString() }))}
          value={year}
          onChange={async (v) => {
            changeMonth({ toYear: v });
          }}
          disabled={disabled}
          className="max-w-[8.8rem] text-ui font-semibold"
        />
      </div>
      <Button
        rightIcon="regularChevronRight"
        variant="secondarySubtle"
        onClick={() => !disabled && changeMonth({ byDistance: 1 })}
        disabled={disabled}
        aria-label="Next month"
      />
    </div>
  );
};
